/**
 * `<RoleBadge>` — chip visual estandarizado para un cargo (MemberRole).
 *
 * Combina icono + color del rol y opcionalmente un sub-chip de sección.
 * Uso principal: listados de miembros, panel de cargos, tarjetas de perfil.
 *
 * Ejemplos:
 *   <RoleBadge role="director" />
 *   <RoleBadge role="tesorero" section="aventureros" showSection />
 *   <RoleBadge role="consejero" size="xs" variant="soft" hideIcon />
 */

import { cn } from "@/lib/utils";
import {
  ROLE_CHIP_CLASSES,
  ROLE_CHIP_SOFT_CLASSES,
  ROLE_ICONS,
} from "@/lib/constants/roles";
import { SectionBadge } from "./SectionBadge";
import {
  CLUB_TYPE_LABELS,
  MEMBER_ROLE_LABELS,
  type ClubType,
  type MemberRole,
} from "@/types";

export interface RoleBadgeProps {
  role: MemberRole;
  section?: ClubType;
  /** Muestra un mini-chip de sección adosado al lado. */
  showSection?: boolean;
  variant?: "solid" | "soft";
  size?: "xs" | "sm" | "md";
  hideIcon?: boolean;
  className?: string;
}

const SIZES: Record<NonNullable<RoleBadgeProps["size"]>, string> = {
  xs: "px-1.5 py-0.5 text-[10px]",
  sm: "px-2 py-0.5 text-xs",
  md: "px-2.5 py-1 text-xs",
};

const ICON_SIZES: Record<NonNullable<RoleBadgeProps["size"]>, string> = {
  xs: "w-2.5 h-2.5",
  sm: "w-3 h-3",
  md: "w-3.5 h-3.5",
};

export function RoleBadge({
  role,
  section,
  showSection = false,
  variant = "solid",
  size = "sm",
  hideIcon = false,
  className,
}: RoleBadgeProps) {
  const palette =
    variant === "soft" ? ROLE_CHIP_SOFT_CLASSES : ROLE_CHIP_CLASSES;
  const Icon = ROLE_ICONS[role];
  const label = MEMBER_ROLE_LABELS[role];
  const ariaLabel = section ? `${label} — ${CLUB_TYPE_LABELS[section]}` : label;

  const badge = (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-semibold rounded-full whitespace-nowrap",
        palette[role],
        SIZES[size],
        className,
      )}
      aria-label={ariaLabel}
    >
      {!hideIcon && Icon && (
        <Icon className={cn("shrink-0", ICON_SIZES[size])} aria-hidden />
      )}
      <span>{label}</span>
    </span>
  );

  if (showSection && section) {
    return (
      <span className="inline-flex items-center gap-1">
        {badge}
        <SectionBadge section={section} variant="soft" size={size} />
      </span>
    );
  }
  return badge;
}

export default RoleBadge;
